/**
 * Notifications inbox sidebar entry (bell) + slide-out panel.
 *
 * The footer hosts an icon-only bell to the left of the alias control; clicking
 * it toggles the secondary inbox panel (durable notification rows + read-time
 * projected Space `@here`/`@role` mentions). Opening the panel advances the
 * durable "seen" cursor. The bell badge counts items newer than that cursor
 * that are still unread, so reading a conversation or Space channel shrinks it
 * without opening the list. Per-item unread styling clears when the row is
 * opened, via "Mark all as read", or when the underlying conversation or Space
 * channel is already read.
 *
 * Guest-safe: `useInbox` returns an inert value when no `InboxProvider` is
 * mounted, so this renders nothing meaningful in the public shell. The button
 * additionally gates on identity status.
 */

import { useEffect, useRef, useCallback, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { INBOX_UNSEEN_COUNT_CAP, type InboxItem } from '@adieuu/shared';
import { useSidebar } from '../../components/Sidebar';
import { Button } from '../../components/Button';
import { Icon } from '../../icons/Icon';
import { useIdentity } from '../../hooks/useIdentity';
import { useInbox } from '../../hooks/useInbox';
import { useReadableSpaceInboxItems } from '../../hooks/useReadableSpaceInboxItems';
import { useInboxNotificationLookups } from '../../hooks/useInboxNotificationLookups';
import { describeInboxItem, type InboxNotificationLookups } from './inboxItemDescriptor';
import { countHiddenUnreadableUnseen } from '../../services/spaceChannelReadability';
import { useSidebarPanelDismiss } from './useSidebarPanelDismiss';
import { sidebarActions } from '../../utils/sidebarActions';
import { Tooltip } from '../../components/Tooltip';

export function InboxSidebarButton({
  isOpen,
  onToggle,
}: {
  isOpen: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const { status: identityStatus } = useIdentity();
  const { items, unseenCount, seenAt } = useInbox();
  const readableItems = useReadableSpaceInboxItems(items);
  const hiddenUnseen = countHiddenUnreadableUnseen(items, readableItems, seenAt);
  const visibleUnseen = Math.max(0, unseenCount - hiddenUnseen);

  if (identityStatus !== 'logged_in') return null;

  const hasUnseen = visibleUnseen > 0;
  // Server caps at INBOX_UNSEEN_COUNT_CAP, so hitting the cap means "at least N".
  const badgeText =
    visibleUnseen >= INBOX_UNSEEN_COUNT_CAP ? `${INBOX_UNSEEN_COUNT_CAP}+` : String(visibleUnseen);
  const ariaLabel = hasUnseen
    ? t('nav.notificationsUnseen', { count: visibleUnseen })
    : t('nav.notifications');

  return (
    <Tooltip content={ariaLabel}>
      <button
        type="button"
        className={`sidebar-inbox-footer-btn${isOpen || hasUnseen ? ' sidebar-inbox-footer-btn-active' : ''}`}
        onClick={onToggle}
        aria-label={ariaLabel}
        aria-pressed={isOpen}
        data-testid="sidebar-inbox-btn"
      >
        <span className="sidebar-inbox-icon">
          <Icon name="bell" />
          {hasUnseen && (
            <span className="sidebar-inbox-badge" aria-hidden="true">
              {badgeText}
            </span>
          )}
        </span>
      </button>
    </Tooltip>
  );
}

/** Short human string for how long ago an item arrived. */
function formatInboxTime(iso: string, t: TFunction): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return t('notifications.time.justNow');
  if (minutes < 60) return t('notifications.time.minutesAgo', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('notifications.time.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  return t('notifications.time.daysAgo', { count: days });
}

function InboxRow({
  item,
  lookups,
  onOpen,
}: {
  item: InboxItem;
  lookups: InboxNotificationLookups;
  onOpen: (item: InboxItem, href?: string, openInvites?: boolean) => void;
}): ReactNode {
  const { t } = useTranslation();
  const { icon, primary, secondary, href, openInvites } = describeInboxItem(item, t, lookups);
  const time = formatInboxTime(item.createdAt, t);

  return (
    <button
      type="button"
      className={`sidebar-inbox-item${item.read ? '' : ' sidebar-inbox-item-unread'}`}
      onClick={() => onOpen(item, href, openInvites)}
    >
      <span className="sidebar-inbox-item-icon">
        <Icon name={icon} />
      </span>
      <span className="sidebar-inbox-item-body">
        <span className="sidebar-inbox-item-topline">
          <span className="sidebar-inbox-item-primary">{primary}</span>
          {time && <span className="sidebar-inbox-item-time">{time}</span>}
          {!item.read && <span className="sidebar-inbox-item-dot" aria-hidden="true" />}
        </span>
        {secondary && <span className="sidebar-inbox-item-secondary">{secondary}</span>}
      </span>
    </button>
  );
}

export function InboxPanel({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { closeMobile } = useSidebar();
  const {
    items,
    isLoading,
    hasMore,
    loadMore,
    markSeen,
    markItemRead,
    markAllRead,
    clearHistory,
  } = useInbox();
  const readableItems = useReadableSpaceInboxItems(items);
  const lookups = useInboxNotificationLookups(readableItems);
  const panelRef = useRef<HTMLDivElement>(null);
  const hasUnread = readableItems.some((item) => !item.read);
  const hasItems = readableItems.length > 0;

  useSidebarPanelDismiss({ isOpen, onClose, panelRef });

  // Opening the inbox acknowledges everything currently newer than the cursor.
  useEffect(() => {
    if (isOpen) void markSeen();
  }, [isOpen, markSeen]);

  const handleOpenItem = useCallback(
    (item: InboxItem, href?: string, openInvites?: boolean) => {
      void markItemRead(item.id);
      if (openInvites) {
        closeMobile();
        onClose();
        sidebarActions.openInvites();
        return;
      }
      if (!href) return;
      closeMobile();
      onClose();
      navigate(href);
    },
    [markItemRead, closeMobile, onClose, navigate],
  );

  if (!isOpen) return null;

  return (
    <div className="sidebar-friends-panel sidebar-inbox-panel" ref={panelRef}>
      <div className="sidebar-friends-panel-header">
        <div className="sidebar-inbox-panel-title-row">
          <span className="sidebar-friends-panel-title">{t('notifications.title')}</span>
          <Button
            variant="ghost"
            size="sm"
            className="sidebar-friends-panel-close"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <Icon name="x" />
          </Button>
        </div>
        {(hasUnread || hasItems) && (
          <span className="sidebar-inbox-panel-actions">
            {hasUnread && (
              <Button
                variant="ghost"
                size="sm"
                className="sidebar-inbox-mark-all"
                onClick={() => void markAllRead()}
              >
                {t('notifications.markAllRead')}
              </Button>
            )}
            {hasItems && (
              <Button
                variant="ghost"
                size="sm"
                className="sidebar-inbox-clear-history"
                onClick={() => void clearHistory()}
              >
                {t('notifications.clearHistory')}
              </Button>
            )}
          </span>
        )}
      </div>

      <div className="sidebar-friends-panel-list">
        {readableItems.length === 0 && !isLoading && (
          <div className="sidebar-friends-panel-empty">{t('notifications.inbox.empty')}</div>
        )}

        {readableItems.map((item) => (
          <InboxRow key={item.id} item={item} lookups={lookups} onOpen={handleOpenItem} />
        ))}

        {hasMore && (
          <button
            type="button"
            className="sidebar-inbox-load-more"
            onClick={() => void loadMore()}
          >
            {t('notifications.inbox.loadMore')}
          </button>
        )}
      </div>
    </div>
  );
}
